import FormConfig from '../ConfigurationFormConfig';
import AjaxUtility from '../../Utility/AjaxUtility';

/**
 * AreaManagerLegacy (DWA-A 138)
 *
 * Responsibility:
 * - addAreaType, removeAreaType
 * - fillAreaTypeData (incl. runoffTypes caching/AJAX)
 * - fillRunoffTypeSelect, fillCoefficientSelect
 * - bindAreaTypeSelectHandlers, updateIndexes, setIndexes, toggleRemoveButtons
 *
 * Notes:
 * - Behaviour is kept 1:1 from ConfigurationForm.js, only extracted into this class.
 * - Calls to updateFormData/updateSummary now go through the provided host (ConfigurationForm) instance.
 *   // CHANGE: Use host.updateFormData() instead of this.updateFormData() because logic moved to manager.
 * - Added console.log at critical actions (add/remove/fetch/cache).
 */
export default class AreaManagerLegacy {
    /**
     * @param {jQuery} form
     * @param {Object} state shared state (unused here but kept for symmetry)
     * @param {Object} host instance of ConfigurationForm (for callbacks)
     */
    constructor(form, state, host) {
        this.form = form;
        this.state = state;
        this.host = host;

        // Local state (copied from original ConfigurationForm)
        // CHANGE: Read centralized values from FormConfig.settings first; keep safe fallbacks
        this.coefficientSteps = (FormConfig.settings && typeof FormConfig.settings.coefficientStep !== 'undefined')
            ? FormConfig.settings.coefficientStep
            : -0.05;
        this.areaTypeMax = (FormConfig.settings && typeof FormConfig.settings.areaTypeMax !== 'undefined')
            ? FormConfig.settings.areaTypeMax
            : 14;
        this.areaTypeCount = $(FormConfig.selectors.areaTypeBlock).length;
        this.runoffTypes = {}; // cache per areaType
    }

	disableIfSingleOption($select) {
		if ($select.length && $select[0]) {
			if ($select[0].options.length === 1) {
				$select.addClass('disabled');
			} else {
				$select.removeClass('disabled');
			}
		}
	}

    bindAreaTypeSelectHandlers() {
        $(FormConfig.selectors.areaTypeSelect).off('change');
        $(FormConfig.selectors.areaTypeSelect).on('change', e => {
            this.fillAreaTypeData($(e.target), parseInt(e.target.value));
			this.resetArea($(e.target));
            this.host.updateFormData(e.target); // CHANGE
        });

        $(FormConfig.selectors.runofftypeSelect).off('change');
        $(FormConfig.selectors.runofftypeSelect).on('change', e => {
            const $areaTypeBlock = $(e.target).parents(FormConfig.selectors.areaTypeBlock);
            const optionData = $(e.target.options[e.target.selectedIndex]).data();
			this.resetArea($(e.target));

            this.fillCoefficientSelect($areaTypeBlock, optionData);
            this.host.updateFormData(e.target); // CHANGE
        });

        $(FormConfig.selectors.coefficientSelect).add(FormConfig.selectors.areaInput).off('change');
        $(FormConfig.selectors.coefficientSelect).add(FormConfig.selectors.areaInput).on('change', e => {
            this.host.updateFormData(e.target); // CHANGE
        });

        $(FormConfig.selectors.removeAreaTypeButton).off('click');
        $(FormConfig.selectors.removeAreaTypeButton).on('click', e => {
            e.preventDefault();
            const $areaTypeBlock = $(e.target).parents(FormConfig.selectors.areaTypeBlock);
            this.removeAreaType($areaTypeBlock);
            return false;
        });
    }

    addAreaType() {
        if (this.areaTypeCount >= this.areaTypeMax) {
            console.log('[AreaManagerLegacy] addAreaType: limit reached', this.areaTypeCount);
            return;
        }

        const $template = $(FormConfig.selectors.areaTypeBlock).last();
        const clone = $template.clone();

        clone.find(FormConfig.selectors.runofftypeSelect)[0].options.length = 0;
        clone.find(FormConfig.selectors.areaInput)[0].value = '';

        $template.after(clone);
        this.areaTypeCount++;
        console.log('[AreaManagerLegacy] addAreaType: new count', this.areaTypeCount);

        this.updateIndexes();

        this.fillAreaTypeData(clone.find(FormConfig.selectors.areaTypeSelect), parseInt(clone.find(FormConfig.selectors.areaTypeSelect)[0].value));

        this.bindAreaTypeSelectHandlers();

        this.toggleRemoveButtons();
    }

    removeAreaType($areaTypeBlock) {
        if ($(FormConfig.selectors.areaTypeBlock).length > 1) {
            $areaTypeBlock.remove();
            this.updateIndexes();
            this.areaTypeCount--;
            console.log('[AreaManagerLegacy] removeAreaType: new count', this.areaTypeCount);
        }
        this.toggleRemoveButtons();
    }

    updateIndexes() {
        const $blocks = $(FormConfig.selectors.areaTypeBlock);
        $blocks.each((index, block) => {
            this.setIndexes($(block).find('select, input'), index);
        });
    }

    setIndexes($elements, index) {
        $elements.each((i, element) => {
            if (element.name) {
                element.name = element.name.replace(/\d/, index);
            }
        });
    }

    toggleRemoveButtons() {
        const removeButtons = $(FormConfig.selectors.removeAreaTypeButton);
        removeButtons.toggle(removeButtons.length !== 1);

        if (this.areaTypeCount >= this.areaTypeMax) {
            $(FormConfig.selectors.addAreaTypeButton).hide();
            $(FormConfig.selectors.addAreaTypeLimitReached).show();
        } else {
            $(FormConfig.selectors.addAreaTypeButton).show();
            $(FormConfig.selectors.addAreaTypeLimitReached).hide();
        }
    }

    fillAreaTypeData($areaTypeSelect, areaType, initialFill) {
        const $areaTypeBlock = $areaTypeSelect.parents(FormConfig.selectors.areaTypeBlock);

        if (this.runoffTypes[areaType] === undefined) {
            console.log('[AreaManagerLegacy] fetch runoffTypes for areaType', areaType);
            AjaxUtility.request('Configurator', {
                controller: 'Configuration',
                action: 'getAreaTypeData',
                areaType: areaType
            }).done(response => {
                let runoffTypes = [];
                try {
                    runoffTypes = JSON.parse(response);
                } catch (e) {
                    console.error('[AreaManagerLegacy] Invalid JSON for runoffTypes', e);
                }
                // cache results
                this.runoffTypes[areaType] = runoffTypes;
                console.log('[AreaManagerLegacy] cached runoffTypes for', areaType, runoffTypes);
                this.fillRunoffTypeSelect($areaTypeBlock, runoffTypes, initialFill);
            });
        } else {
            console.log('[AreaManagerLegacy] use cached runoffTypes for areaType', areaType);
            this.fillRunoffTypeSelect($areaTypeBlock, this.runoffTypes[areaType], initialFill);
        }
    }

	resetArea($select) {
		const $areaTypeBlock = $select.parents(FormConfig.selectors.areaTypeBlock),
			$areaInput = $areaTypeBlock.find(FormConfig.selectors.areaInput);

		$areaInput.val('');
	}

    fillRunoffTypeSelect($areaTypeBlock, runoffTypes, initialFill) {
        const $runoffTypeSelect = $areaTypeBlock.find(FormConfig.selectors.runofftypeSelect);
        if ($runoffTypeSelect[0] !== undefined) {
            const runoffTypeSelect = $runoffTypeSelect[0];
            runoffTypeSelect.options.length = 0; // clear options

            runoffTypes.forEach(element => {
                $runoffTypeSelect[0].add(
                    // Reuse host helper for consistency
                    this.host.createOptionElement(element.uid, element.name, [
                        { key: 'data-coefficientfrom', value: element.coefficientFrom },
                        { key: 'data-coefficientto', value: element.coefficientTo }
                    ])
                );
            });

            const initialValue = $areaTypeBlock.find(FormConfig.selectors.runofftypeValue).data('value');
            if (initialFill && initialValue) {
                $runoffTypeSelect.val($areaTypeBlock.find(FormConfig.selectors.runofftypeValue).data('value'));
            }

            this.fillCoefficientSelect($areaTypeBlock, $(runoffTypeSelect.options[runoffTypeSelect.selectedIndex]).data(), initialFill);
        }
    }

    fillCoefficientSelect($areaTypeBlock, optionData, initialFill) {
        const $coefficientSelect = $areaTypeBlock.find(FormConfig.selectors.coefficientSelect);
        const coefficientSelect = $coefficientSelect[0];
        let coefficient = optionData.coefficientto;

        // clear options
        coefficientSelect.options.length = 0;

        while (coefficient >= optionData.coefficientfrom) {
            coefficientSelect.options.add(
                this.host.createOptionElement(coefficient, coefficient)
            );
            // floating point shenanigans
            coefficient = Math.round((coefficient + this.coefficientSteps) * 100) / 100;
        }

        const initialValue = $areaTypeBlock.find(FormConfig.selectors.coefficientValue).data('value');
        if (initialFill && initialValue) {
            $coefficientSelect.val($areaTypeBlock.find(FormConfig.selectors.coefficientValue).data('value'));
        }

		this.disableIfSingleOption($coefficientSelect);
    }
}
